import { useEffect, useState } from "react";
import { useNavigate, Routes, Route, useParams } from "react-router-dom";
import { groupBy } from "lodash";

import { useMutation, useQuery } from "../../hooks";
import StepSorter from "../StepSorter/StepSorter";
import Results from "../Results/Results";
import Steps from "./Steps";
import { Term } from "../../utils/interfaces";

type StepData = {
  1: Term[];
  2: Term[];
  3: Term[];
};

export default function Exercise() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [values, setValues] = useState("");
  const [valuesStepData, setValuesStepData] = useState<StepData>({
    1: [],
    2: [],
    3: [],
  });

  const [strengths, setStrengths] = useState("");
  const [strengthsStepData, setStrengthsStepData] = useState<StepData>({
    1: [],
    2: [],
    3: [],
  });

  const { data: terms } = useQuery("terms");
  const groupedTerms = groupBy(terms, "category");
  const valuesOptions = groupedTerms?.value || [];
  const strengthsOptions = groupedTerms?.strength || [];

  const queryResponse = useQuery("prompt_results", id);
  const { data: promptResult } = queryResponse;

  const submitToPrompt = useMutation("prompt_results", promptResult?.id);

  useEffect(() => {
    if (values.length > 0) handleValuesSubmit();
  }, [values]);

  useEffect(() => {
    if (strengths.length > 0) handleStrengthsSubmit();
  }, [strengths]);

  const makeSubmit = (nextPath: string) => {
    const handleSubmitSuccess = (result: any) => {
      navigate(nextPath);
    };
    const handleSubmit = () => {
      const mutationValues = { values, strengths };
      submitToPrompt.mutate(mutationValues, { onSuccess: handleSubmitSuccess });
    };
    return handleSubmit;
  };

  const handleValuesSubmit = makeSubmit(`/exercise/${id}/strengths`);
  const handleStrengthsSubmit = makeSubmit(`/exercise/${id}/results`);

  const valuesHeader = (
    <>
      <h1>Identifying Your Values</h1>
      <p>
        Values are the principles that guide your decisions and shape who you
        want to become. In this exercise, you'll explore what matters most to
        you at your core. Consider each value carefully and rate how strongly it
        resonates with your life and aspirations. There are no right or wrong
        answers—focus on what truly matters to you.
      </p>
    </>
  );
  const strengthsHeader = (
    <>
      <h1>Identifying Your Strengths</h1>
      <p>
        Strengths are the natural abilities and qualities that make you uniquely
        effective. This exercise helps you recognize what you're naturally good
        at, even if you don't always acknowledge these gifts. For each strength,
        reflect honestly on how accurately it describes you. Think about times
        when others have appreciated these qualities in you or when you've felt
        most capable and fulfilled.
      </p>
    </>
  );
  const resultsHeader = (
    <>
      <h1>Integrated Life Purpose Statement</h1>
    </>
  );

  const valuesComponent = valuesOptions.length && (
    <StepSorter
      items={valuesOptions}
      key="values"
      label="values"
      nextLabel="Continue to Strengths"
      stepData={valuesStepData}
      setStepData={setValuesStepData}
      setItems={setValues}
      sortCategories={[
        { label: "Not Important", name: "notImportant", rank: 3 },
        { label: "Important", name: "important", rank: 2 },
        { label: "Very Important", name: "veryImportant", rank: 1 },
      ]}
    />
  );

  const strengthsComponent = strengthsOptions.length && (
    <StepSorter
      items={strengthsOptions}
      key="strengths"
      label="strengths"
      nextLabel="Generate Results"
      setItems={setStrengths}
      stepData={strengthsStepData}
      setStepData={setStrengthsStepData}
      sortCategories={[
        { label: "Very Untrue", name: "veryUntrue", rank: 5 },
        { label: "Untrue", name: "untrue", rank: 4 },
        { label: "Neutral", name: "neutral", rank: 3 },
        { label: "True", name: "true", rank: 2 },
        { label: "Very True", name: "veryTrue", rank: 1 },
      ]}
    />
  );

  return (
    <div>
      <Steps />

      <Routes>
        <Route path="/results" element={resultsHeader} />
        <Route path="/strengths/*" element={strengthsHeader} />
        <Route path="/" element={valuesHeader} />
      </Routes>

      <Routes>
        <Route path="/results" element={<Results />} />
        <Route path="/strengths" element={strengthsComponent} />
        <Route path="/" element={valuesComponent} />
      </Routes>
    </div>
  );
}
